import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Divider, Stack, TextField, ToggleButton, Tooltip, Typography } from '@mui/material'
// El mismo icono con el que el menu lateral rotula RESOURCES (LeftMenu.jsx): el usuario ya lo
// asocia a materiales, asi que no se inventa uno nuevo.
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import { apiGet } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { GetAccumulatedUtcTime } from '../../generic/DateTimeFunctions'
import ProcessTreeInspector from './ProcessTreeInspector'
import ProcessTreeInspectorDock from './ProcessTreeInspectorDock'
import ProcessTreeJobDialog from './ProcessTreeJobDialog'
import ProcessTreeRow, { BRANCH_COL_PX } from './ProcessTreeRow'
import SurfaceResizeHandle, { SURFACE_INITIAL_HEIGHT } from './SurfaceResizeHandle'
import {
  PROCESS_TREE_ROW_KIND,
  buildProcessTreeRows,
  collectProcessTotals,
  collectRequiredPredecessorIds,
  findLevelNodes,
  resolveTopLevelNodeId,
  selectVisibleRows,
  containingSetKeys
} from './processTree'
import { resolveProcessTreeJobGates, useProcessTreeJobWriters } from './useProcessTreeJobWriters'
import { useTreeFolding } from './useTreeFolding'
import { SURFACE_BAR_HEIGHT_PX } from './WorkflowSurfaceSwitch'
import {
  applyHydratedSubWorkflows,
  collectUnhydratedSets,
  fetchWorkflowForProcessTree,
  hydrateProcessTree
} from './processTreeHydration'
import { buildCoverageContext } from './processTreeCoverage'
import { applyNodeSelection } from './bomTree'
// The tab the job window opens on (RN146.2), named by the same constant the panel and the window use.
import { TAB } from './ProcessTreeInspectorParts'
import { NIGHT_JOB_CONTEXT_OFF } from './jobNightCharacter'

/**
 * ProcessTreeView.jsx
 * The process Tree surface of the Workflow 2.0 editor (story 118). It is a LENS over the model the editor
 * holds — unsaved changes included (RN6) — and, since the PO reviews of 2026-09-10/11, also a place to
 * EDIT it: the inspector and the operation dialog write through the graph's own reducer actions, under the
 * graph's own gates (edit mode, permissions, a top-level job). What it never does is persist: the editor's
 * Save does that, for the graph and the tree alike.
 *
 * Three things this file owns and nothing else does:
 *  - RN35-RN38 — the sets that arrive without their operations are loaded HERE, into local state, never
 *    into the reducer model: writing them there would travel in the save PUT as a user change (risk R4).
 *    A subworkflow that fails is remembered as failed and not asked for again in the same entry.
 *  - RN33/RN34/POL5 — the header figures describe the COMPLETE process. Collapsing, searching and the
 *    Material button cannot change them, which is why they come from `collectProcessTotals` (the model)
 *    and not from the rows on screen.
 *  - RN71/POL1 — the two warnings live at the FOOTER, stack, and never replace the tree: a process with
 *    defects is still fully drawn above them.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 */

const EMPTY_HYDRATION = { subWorkflowsById: {}, failedSetNodeIds: [] }
/** Story 224: the default transform of a loaded subworkflow leaves it as it came. */
const AS_LOADED = (nodeId, loaded) => loaded
/** RN49: the BOM is not even requested until the Material button is turned on, and only once. */
const EMPTY_BOM = { loaded: false, loading: false, lines: [] }

const isSelectableRow = (row) => row.kind === PROCESS_TREE_ROW_KIND.JOB || row.kind === PROCESS_TREE_ROW_KIND.SET

const findNodeById = (nodeId, nodes) => (nodeId ? findLevelNodes(nodeId, nodes).find((node) => node.id === nodeId) || null : null)

const ProcessTreeView = ({
  workflow = {},
  currentNode = null,
  dispatchWorkflowData = () => {},
  nodes = [],
  setNodes = () => {},
  frontendConfig = {},
  isUserAuthorized = () => false,
  showAlert = () => {},
  active = false,
  detailedBranches = false,
  editing = false,
  canEnterEditMode = false,
  onEnterEditMode = () => {},
  updateJobReference = () => {},
  updateTaskList = () => {},
  // Story #150: the job dialog hosts the full resources panel now, so what that panel needs travels too.
  resourceTypes = [],
  parentPartReference = null,
  // RN117.3: reports (hostId, isOpen) for every table this surface mounts, so the editor's save guard
  // keeps one entry per host instead of a flag they all overwrite.
  onEditingRowChanged = () => {},
  // Threaded through to the extracted task table, which the side panel and the job dialog now mount.
  // It reads getTempUUID in the body of its JSX, so a missing one throws on the first render — hence
  // the no-op defaults here as well as in the component itself.
  // Story #150 (S2/S5): the workflow object is rewritten in place by the reducer, so its identity is
  // no signal that it changed. This counter is, and it is what makes the memos below recompute.
  modelRevision = 0,
  workflowName = '',
  partReference = null,
  getTempUUID = () => undefined,
  setTempUUID = () => {},
  progressCalculationTypes = [],
  indexValues = [],
  workflowUseIndex = false,
  // Story 208 (S31): SHOW_TASK_MACHINE_TIME, handed to both hosts of the task table (panel and window).
  showMachineTime = false,
  featureGroupRevisionControlled = false,
  featureGroupBaselines = {},
  cascadeApprovalEnabled = false,
  fabricationOrderOperable = true,
  // Story 183 (RN7): the editor's single night context, the same object the graph's job panel gets.
  nightJobContext = NIGHT_JOB_CONTEXT_OFF,
  // #167: room the screen keeps at the right of the toolbar for its Graph | Tree tabs; 0 when it has none.
  headerSlotPx = 0,
  // #215: the height the user set with the bar, shared with the graph (D2: 50vh until dragged).
  surfaceHeight = SURFACE_INITIAL_HEIGHT,
  onSurfaceHeightChange = () => {},
  // Story 224 (DL25-a): pure transform of each subworkflow the view loads on its own, before it is overlaid on the model (default: as loaded).
  loadedSubWorkflowContent = AS_LOADED
}) => {
  const { t_ } = useSmartTranslation()
  const [hydration, setHydration] = useState(EMPTY_HYDRATION)
  const [openSetKeys, setOpenSetKeys] = useState(() => new Set())
  const [closedParallelKeys, setClosedParallelKeys] = useState(() => new Set())
  const [selectedRowKey, setSelectedRowKey] = useState('')
  const [editingRowKey, setEditingRowKey] = useState('')
  // RN146.2: the same window, opened on the tab the user asked for. The ordinary second click on a row
  // opens it on General; the sidebar's access to the resources opens it on Material.
  const [editingTab, setEditingTab] = useState(TAB.GENERAL)
  const [pendingScrollKey, setPendingScrollKey] = useState('')
  const [query, setQuery] = useState('')
  const [materialEnabled, setMaterialEnabled] = useState(false)
  // PO review 2026-09-21: los jobs cuyo material esta abierto del todo. Vacio = todos ensenan una linea
  // y su pie «+X materials». Por clave de fila, como el resto de plegados de esta pantalla.
  const [expandedMaterialKeys, setExpandedMaterialKeys] = useState(() => new Set())
  /**
   * PO review 2026-09-22: el conjunto que se acaba de ABRIR. Sus filas bajan en cascada en vez de
   * aparecer de golpe, como ya hacen las de material. Hace falta recordarlo porque la animacion no puede
   * ir en todas las filas: en la primera pintada del proceso montan todas a la vez y el arbol entero se
   * pondria a caer, que es ruido y no explica nada. Se vacia al cerrar.
   */
  const [bom, setBom] = useState(EMPTY_BOM)
  const hydratingRef = useRef(false)
  /**
   * Viva mientras el componente lo este. NO se puede usar la limpieza del efecto de hidratacion para
   * cortar la carga: ese efecto depende de `hydration`, y publicar cada subworkflow segun llega lo
   * cambia — la carga en vuelo se cancelaria a si misma en la primera publicacion y el arbol se quedaria
   * a medias. Lo unico que corta de verdad es desmontar.
   */
  // La fila marcada. `liftCandidateRef` recuerda la ULTIMA que fue de material: cuando desaparece ya no
  // esta en `rows` para preguntarle de quien colgaba.
  const selectedRowRef = useRef(null)
  const liftCandidateRef = useRef(null)
  const surfaceRef = useRef(null)
  const aliveRef = useRef(true)
  // Se vuelve a marcar viva AL MONTAR, no solo muerta al desmontar: en desarrollo StrictMode monta,
  // limpia y vuelve a montar, y sin esta linea la bandera se quedaba en false para siempre — la carga
  // publicaba en el vacio y el arbol no se llenaba nunca.
  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])
  const syncedRef = useRef(false)
  const rowsRef = useRef(null)

  const showDuration = isUserAuthorized('WORKFLOW_JOBS')
  const showTasks = isUserAuthorized('WORKFLOW_TASKS')
  const showMaterial = isUserAuthorized('RESOURCES')

  // RN37: the cache is overlaid on the LIVE model on every render, so the lens never holds a stale copy.
  const effectiveWorkflow = useMemo(
    () => applyHydratedSubWorkflows(workflow, hydration.subWorkflowsById, loadedSubWorkflowContent),
    [workflow, hydration.subWorkflowsById, modelRevision, loadedSubWorkflowContent]
  )

  // RN48/RN50: no coverage context while the button is off — the rows then say "no data", never zero.
  const coverage = useMemo(
    () => (materialEnabled && bom.loaded ? buildCoverageContext(bom.lines, effectiveWorkflow?.nodes) : null),
    [materialEnabled, bom.loaded, bom.lines, effectiveWorkflow]
  )

  // The inspector's material does not wait for the Material button: it only needs the BOM to be loaded.
  const inspectorCoverage = useMemo(
    () => (bom.loaded ? buildCoverageContext(bom.lines, effectiveWorkflow?.nodes) : null),
    [bom.loaded, bom.lines, effectiveWorkflow]
  )

  const { rows, warnings } = useMemo(
    () => buildProcessTreeRows({ workflow: effectiveWorkflow, coverage, failedSetNodeIds: hydration.failedSetNodeIds, detailedBranches, expandedMaterialKeys }),
    [effectiveWorkflow, coverage, hydration.failedSetNodeIds, detailedBranches, expandedMaterialKeys, modelRevision]
  )

  // RN33/POL5: the totals read the model, never the rows on screen.
  const totals = useMemo(
    () => collectProcessTotals(effectiveWorkflow, { failedSetNodeIds: hydration.failedSetNodeIds }),
    [effectiveWorkflow, hydration.failedSetNodeIds, modelRevision]
  )

  // RN39: a set arrives collapsed; a Parallel row arrives open.
  const isCollapsed = useCallback((row) => {
    if (row.kind === PROCESS_TREE_ROW_KIND.SET) {
      return !openSetKeys.has(row.key)
    }
    // La fila Paralelo y las cabeceras de camino se cierran igual: por clave, y abiertas de partida.
    return (row.kind === PROCESS_TREE_ROW_KIND.PARALLEL || row.kind === PROCESS_TREE_ROW_KIND.BRANCH) && closedParallelKeys.has(row.key)
  }, [openSetKeys, closedParallelKeys])

  const { visibleRows, hasMatches } = useMemo(
    () => selectVisibleRows(rows, { isCollapsed, query, materialEnabled }),
    [rows, isCollapsed, query, materialEnabled]
  )

  // Story 167 (PO, 29/09/2026): the rows of a set fall into place when it opens and rise away when it closes — the
  // same motion, and the same rule of which rows are inside it, as the tree of the graph's panel (`useTreeFolding`).
  const { displayedRows, enterIndexByKey, leaveIndexByKey, setOpened, setClosed } = useTreeFolding(visibleRows)

  // RN40: one button, and what it offers depends on whether ANYTHING foldable is folded.
  const anyCollapsed = useMemo(() => rows.some((row) => row.collapsible && isCollapsed(row)), [rows, isCollapsed])

  /**
   * RN35/RN36/RN38: one hydration pass per entry, tolerant to failures. The pending list excludes what is
   * already cached AND what already failed, which is what stops a failed set from being asked for again
   * (S8/S9) and what makes the effect settle instead of looping on its own result.
   */
  useEffect(() => {
    if (!active || hydratingRef.current) {
      return undefined
    }
    const pending = collectUnhydratedSets(effectiveWorkflow?.nodes).filter((entry) =>
      !hydration.subWorkflowsById[entry.subWorkflowId] && !hydration.failedSetNodeIds.includes(entry.nodeId))
    if (pending.length === 0) {
      return undefined
    }
    hydratingRef.current = true
    /**
     * PO review 2026-09-22: cada subworkflow se publica EN CUANTO llega, no los 27 al final. El arbol se
     * rellena a la vista en vez de quedarse con guiones hasta que termina el ultimo. El efecto se vuelve
     * a lanzar con cada publicacion, y `hydratingRef` lo devuelve en el acto mientras la carga sigue.
     */
    const publish = (progress) => {
      if (!aliveRef.current) {
        return
      }
      setHydration((previous) => (progress.subWorkflowId
        ? { ...previous, subWorkflowsById: { ...previous.subWorkflowsById, [progress.subWorkflowId]: progress.subWorkflow } }
        : { ...previous, failedSetNodeIds: [...new Set([...previous.failedSetNodeIds, progress.failedSetNodeId])] }))
    }
    const run = async () => {
      const result = await hydrateProcessTree(effectiveWorkflow, fetchWorkflowForProcessTree, hydration.subWorkflowsById, publish)
      hydratingRef.current = false
      if (!aliveRef.current) {
        return
      }
      // El cierre: lo que ya se publico por el camino no cambia, y lo que fallara despues de la ultima
      // publicacion entra aqui. Sin el, un fallo en la ultima peticion podria quedarse sin marcar.
      setHydration((previous) => ({
        subWorkflowsById: { ...previous.subWorkflowsById, ...result.subWorkflowsById },
        failedSetNodeIds: [...new Set([...previous.failedSetNodeIds, ...result.failedSetNodeIds])]
      }))
    }
    run()
    return undefined
  }, [active, effectiveWorkflow, hydration])

  /**
   * RN49 (amended 2026-09-10): the BOM is fetched once, on the FIRST of two triggers — the Material button,
   * or a job being selected, because the inspector shows the material of the selected job always. Turning
   * the button off and on again asks nothing (RN47). A load that fails says so and leaves the button OFF —
   * the tree stays (error E3). Resolves to whether the lines are available.
   */
  const loadBom = useCallback(async () => {
    if (bom.loaded) {
      return true
    }
    if (bom.loading) {
      return false
    }
    setBom((previous) => ({ ...previous, loading: true }))
    try {
      const lines = await apiGet('workflowResource/' + workflow?.tempWorkflowUUID + (workflow?.id ? '/' + workflow.id : ''))
      setBom({ loaded: true, loading: false, lines: Array.isArray(lines) ? lines : [] })
      return true
    } catch (error) {
      setBom(EMPTY_BOM)
      showAlert({ title: t_('Error loading the BOM'), severity: 'error', message: '' })
      return false
    }
  }, [bom.loaded, bom.loading, workflow, showAlert, t_])

  const toggleMaterial = useCallback(async () => {
    if (materialEnabled) {
      setMaterialEnabled(false)
      // Apagar el boton recoge lo abierto: al volver a encenderlo se ensena otra vez una linea por job,
      // que es lo que el usuario espera de un boton que se apaga.
      setExpandedMaterialKeys(new Set())
      return
    }
    const available = await loadBom()
    setMaterialEnabled(available)
  }, [materialEnabled, loadBom])

  /**
   * RN61/RN62/RN63: the tree selection lands on the row that was clicked, at any depth; the EDITOR one can
   * only land on a first-level node, so a nested operation hands the canvas its containing set. The canvas
   * is PATCHED (`applyNodeSelection`), never rebuilt: rebuilding it would jump the pan and the zoom (R1).
   * Nothing else is dispatched — a selection must not make the process dirty (RN62.1).
   */
  const selectRow = useCallback((row) => {
    setSelectedRowKey(row.key)
    // PO review 2026-09-22: una linea de material se LEE en el panel y se EDITA en el modal del job.
    // Abrir el modal desde aqui dejaba la seleccion en la fila de material, y `canEditMaterial`
    // (abajo, RN131) exige un JOB seleccionado: el modal salia en solo lectura hasta en modo edicion.
    // La linea no tiene nodo propio, asi que el grafo sigue a la operacion que la consume — es la que
    // el panel pasa a inspeccionar.
    const nodeRow = row.kind === PROCESS_TREE_ROW_KIND.MATERIAL
      ? rows.find((candidate) => candidate.key === row.parentKey) || null
      : row
    if (!nodeRow) {
      return
    }
    const topLevelNodeId = resolveTopLevelNodeId(nodeRow.nodeId, effectiveWorkflow?.nodes)
    const topLevelNode = (workflow?.nodes || []).find((node) => node.id === topLevelNodeId)
    if (!topLevelNode) {
      return
    }
    dispatchWorkflowData({ type: 'SET_CURRENT_NODE', node: topLevelNode })
    setNodes(applyNodeSelection(nodes, topLevelNodeId))
  }, [rows, effectiveWorkflow, workflow, nodes, dispatchWorkflowData, setNodes])

  // PO review 2026-09-11: the second click on the selected operation opens its editor.
  const openRow = useCallback((row) => {
    if (row.kind === PROCESS_TREE_ROW_KIND.JOB) {
      setEditingTab(TAB.GENERAL)
      setEditingRowKey(row.key)
      return
    }
    // DILEMA 10 (PO, 2026-09-23): el segundo clic sobre una linea de material abre la ventana de SU
    // operacion en Material. La ventana cuelga siempre de una fila de job — es lo que `canEditMaterial`
    // exige (RN225) y lo que hacia que saliera en solo lectura cuando colgaba de la linea.
    if (row.kind === PROCESS_TREE_ROW_KIND.MATERIAL) {
      setEditingTab(TAB.MATERIAL)
      setEditingRowKey(row.parentKey)
    }
  }, [])

  /**
   * PO review 2026-09-21: el pie abre y recoge el material de SU job y de ninguno mas. Abierto se queda
   * puesto como «ver menos», que es el camino de vuelta; apagar el boton Material tambien lo recoge todo.
   */
  const expandMaterial = useCallback((row) => {
    setExpandedMaterialKeys((previous) => {
      const next = new Set(previous)
      if (next.has(row.materialOwnerKey)) {
        next.delete(row.materialOwnerKey)
      } else {
        next.add(row.materialOwnerKey)
      }
      return next
    })
  }, [])

  const toggleRow = useCallback((row) => {
    const setKeys = row.kind === PROCESS_TREE_ROW_KIND.SET ? setOpenSetKeys : setClosedParallelKeys
    if (row.kind === PROCESS_TREE_ROW_KIND.SET) {
      if (openSetKeys.has(row.key)) {
        setClosed(row)
      } else {
        setOpened(row)
      }
    }
    setKeys((previous) => {
      const next = new Set(previous)
      if (next.has(row.key)) {
        next.delete(row.key)
      } else {
        next.add(row.key)
      }
      return next
    })
  }, [openSetKeys, setOpened, setClosed])

  /**
   * RN40: `Expand all` opens every set at any depth and every parallel group; `Collapse sets` folds the
   * sets and leaves the parallel groups as they are — they were already open for the button to say so.
   */
  const toggleAllRows = useCallback(() => {
    if (!anyCollapsed) {
      setOpenSetKeys(new Set())
      return
    }
    setOpenSetKeys(new Set(rows.filter((row) => row.kind === PROCESS_TREE_ROW_KIND.SET).map((row) => row.key)))
    setClosedParallelKeys(new Set())
  }, [anyCollapsed, rows])

  // RN62/RN63: a chip of the inspector selects the node it names, on its first developed row.
  const selectNodeById = useCallback((nodeId) => {
    const target = rows.find((row) => row.nodeId === nodeId && isSelectableRow(row))
    if (!target) {
      return
    }
    const byKey = new Map(rows.map((row) => [row.key, row]))
    const ancestors = containingSetKeys(target, byKey)
    if (ancestors.length > 0) {
      setOpenSetKeys((previous) => new Set([...previous, ...ancestors]))
    }
    // Si la operacion vive en un camino que el usuario habia cerrado, se abre: saltar a una fila y
    // dejarla escondida seria peor que no saltar.
    const closedContainers = []
    let walker = byKey.get(target.parentKey)
    while (walker) {
      if (walker.kind === PROCESS_TREE_ROW_KIND.PARALLEL || walker.kind === PROCESS_TREE_ROW_KIND.BRANCH) {
        closedContainers.push(walker.key)
      }
      walker = byKey.get(walker.parentKey)
    }
    if (closedContainers.length > 0) {
      setClosedParallelKeys((previous) => new Set([...previous].filter((closed) => !closedContainers.includes(closed))))
    }
    setPendingScrollKey(target.key)
    selectRow(target)
  }, [rows, selectRow])

  /**
   * RN64: entering the surface adopts the selection the graph already has — opening just the sets that
   * contain it and scrolling its row into view. It DISPATCHES NOTHING: the editor already holds that
   * selection, and re-announcing it would be noise. Retried while no row matches yet, because the row of a
   * nested operation only exists once its set finishes hydrating.
   */
  useEffect(() => {
    if (!active) {
      syncedRef.current = false
      return
    }
    if (syncedRef.current) {
      return
    }
    if (!currentNode?.id) {
      syncedRef.current = true
      return
    }
    const target = rows.find((row) => row.nodeId === currentNode.id && isSelectableRow(row))
    if (!target) {
      return
    }
    syncedRef.current = true
    const ancestors = containingSetKeys(target, new Map(rows.map((row) => [row.key, row])))
    if (ancestors.length > 0) {
      setOpenSetKeys((previous) => new Set([...previous, ...ancestors]))
    }
    setSelectedRowKey(target.key)
    setPendingScrollKey(target.key)
  }, [active, currentNode, rows])

  // The row is scrolled once it is actually on screen — which may be a render after the sets were opened.
  useEffect(() => {
    if (!pendingScrollKey) {
      return
    }
    const element = rowsRef.current?.querySelector('[data-key="' + pendingScrollKey + '"]')
    if (!element) {
      return
    }
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
    setPendingScrollKey('')
  }, [pendingScrollKey, visibleRows])

  const selectedRow = rows.find((row) => row.key === selectedRowKey) || null
  /**
   * PO review 2026-09-22: una fila de material no tiene inspector propio. Lo que se inspecciona es la
   * OPERACION que la consume, con su pestana Material delante y la linea marcada: una sola ventana de
   * material en toda la pantalla. `selectedRow` se queda en la linea — es la fila marcada en el arbol y
   * la que gobierna las puertas de edicion, que siguen exigiendo un job.
   */
  const inspectedRow = selectedRow?.kind === PROCESS_TREE_ROW_KIND.MATERIAL
    ? rows.find((row) => row.key === selectedRow.parentKey) || null
    : selectedRow
  const selectedNode = findNodeById(inspectedRow?.nodeId, effectiveWorkflow?.nodes)
  // RN63: the first-level node the EDITOR holds for the selection — the node itself, or the set that contains it.
  const selectedTopLevelId = inspectedRow ? resolveTopLevelNodeId(inspectedRow.nodeId, effectiveWorkflow?.nodes) : ''
  const selectedNested = inspectedRow ? selectedTopLevelId !== inspectedRow.nodeId : false
  const selectedLevelNodes = inspectedRow ? findLevelNodes(inspectedRow.nodeId, effectiveWorkflow?.nodes) : []
  /**
   * The sets the selection lives inside, outermost first — the trail the inspector prints above the
   * identity. `containingSetKeys` answers innermost first, which is the order the tree needs to open them
   * in and the reverse of the order a reader walks them.
   */
  const selectedTrail = selectedRow
    ? containingSetKeys(selectedRow, new Map(rows.map((row) => [row.key, row])))
      .map((key) => rows.find((row) => row.key === key)?.code || '')
      .filter((code) => code !== '')
      .reverse()
    : []

  /**
   * PO review 2026-09-11: the operations the selected one depends on — every OK predecessor at any
   * distance, from the model (`collectRequiredPredecessorIds`) — as a set for the rows to paint and as a
   * list, in tree order, for the inspector to name. A reference row of a required operation is painted
   * too: it IS that operation, wherever the tree happened to draw it.
   */
  /*
   * B9 del Judge (2026-09-23): tambien sobre la OPERACION INSPECCIONADA. Con una linea de material
   * marcada, `selectedRow` no es seleccionable y estos se apagaban: desaparecian «Depends on», el tinte
   * de las filas de las que depende, y el camino en el panel. Se perdia informacion de la operacion que
   * el panel esta ensenando, por mirar la fila marcada en vez de a quien se inspecciona (INV118).
   */
  const requiredIds = useMemo(
    () => (inspectedRow && isSelectableRow(inspectedRow) ? collectRequiredPredecessorIds(inspectedRow.nodeId, effectiveWorkflow?.nodes) : new Set()),
    [inspectedRow, effectiveWorkflow]
  )
  const requiredNodes = useMemo(() => {
    const named = new Set()
    const list = []
    rows.forEach((row) => {
      if (isSelectableRow(row) && requiredIds.has(row.nodeId) && !named.has(row.nodeId)) {
        named.add(row.nodeId)
        list.push({ nodeId: row.nodeId, label: row.code || row.name || row.nodeId })
      }
    })
    return list
  }, [rows, requiredIds])
  const isDependencyRow = (row) => row.nodeId !== '' && row.nodeId !== inspectedRow?.nodeId && requiredIds.has(row.nodeId) &&
    (isSelectableRow(row) || row.kind === PROCESS_TREE_ROW_KIND.REFERENCE)

  // Material always visible: selecting a job fetches the BOM if nobody did yet (RN49 amended).
  useEffect(() => {
    if (active && showMaterial && selectedRow?.kind === PROCESS_TREE_ROW_KIND.JOB && !bom.loaded && !bom.loading) {
      loadBom()
    }
  }, [active, showMaterial, selectedRow, bom.loaded, bom.loading, loadBom])

  /**
   * The tree edits with the graph's own gates — WORKFLOW_JOBS and a job whose first-level node is the
   * reducer's current node — but, since the PO review of 2026-09-11, it does NOT wait for the header's
   * Edit button: whenever the process admits editing (`canEnterEditMode`) the fields are real controls,
   * and the first change enters edit mode by itself (`onEnterEditMode`, the same thing the button does).
   *
   * A NESTED job (inside a set) edits too, through a different door: `UPDATE_JOB_REFERENCE` and
   * `UPDATE_TASKS` write into `currentNode`, which for a nested selection is the containing set (RN63),
   * so its writes go to `UPDATE_NESTED_JOB` (by node id, at any depth) after the set's operations —
   * loaded by this view outside the model (RN35) — have been ADOPTED into it (`ADOPT_SUBWORKFLOW`), the
   * way an accepted subworkflow dialog writes back into its node. The parent's save then carries them.
   */
  const gates = resolveProcessTreeJobGates({
    editing,
    canEnterEditMode,
    canSeeJobs: showDuration,
    canSeeTasks: showTasks,
    canSeeMaterial: showMaterial,
    isJobRow: inspectedRow?.kind === PROCESS_TREE_ROW_KIND.JOB,
    node: selectedNode,
    currentNodeId: currentNode?.id,
    topLevelId: selectedTopLevelId,
    nested: selectedNested
  })
  const canEdit = gates.canEdit
  const canEditTasks = gates.canEditTasks
  const canEditMaterial = gates.canEditMaterial
  // Why the fields are divs right now, in the words the Extern checkbox of the graph already uses.
  const editHint = !gates.editAllowed ? t_('The workflow is not being edited') : ''

  const { writeJobReference, writeTaskList, updateNodeName, updateNodeCode, updateTechnology } = useProcessTreeJobWriters({
    workflow,
    effectiveWorkflow,
    editing,
    onEnterEditMode,
    dispatchWorkflowData,
    updateJobReference,
    updateTaskList,
    nodes,
    setNodes,
    inspectedNodeId: inspectedRow?.nodeId || '',
    nested: selectedNested
  })

  /**
   * The selected BRANCH as the inspector reads it: where it opens, where it closes, the operations that
   * hang from it (any depth) and its siblings. Null unless a branch header is what is selected.
   */
  const selectedBranch = useMemo(() => {
    if (!selectedRow || selectedRow.kind !== PROCESS_TREE_ROW_KIND.BRANCH) {
      return null
    }
    const byKey = new Map(rows.map((row) => [row.key, row]))
    const descendsFromBranch = (row) => {
      let walker = byKey.get(row.parentKey)
      while (walker) {
        if (walker.key === selectedRow.key) {
          return true
        }
        walker = byKey.get(walker.parentKey)
      }
      return false
    }
    const jobs = rows
      .filter((row) => row.kind === PROCESS_TREE_ROW_KIND.JOB && descendsFromBranch(row))
      .map((row) => findNodeById(row.nodeId, effectiveWorkflow?.nodes))
      .filter((jobNode) => jobNode !== null)
    const siblings = rows
      .filter((row) => row.kind === PROCESS_TREE_ROW_KIND.BRANCH && row.parentKey === selectedRow.parentKey && row.key !== selectedRow.key)
      .map((row) => ({ key: row.key, index: row.branchIndex }))
    return {
      index: selectedRow.branchIndex,
      count: selectedRow.branchCount,
      splitNode: findNodeById(selectedRow.splitNodeId, effectiveWorkflow?.nodes),
      joinNode: findNodeById(selectedRow.joinNodeId, effectiveWorkflow?.nodes),
      jobs,
      siblings
    }
  }, [selectedRow, rows, effectiveWorkflow])

  const selectRowByKey = useCallback((key) => {
    // PO review 2026-09-22: se llega aqui desde el panel, asi que la fila puede estar fuera de la parte
    // visible del arbol. Se marca la clave y el efecto de abajo la trae — el mismo camino que usa el
    // salto desde el grafo, que ya esperaba a que la fila existiese en pantalla.
    setPendingScrollKey(key)
    const row = rows.find((candidate) => candidate.key === key)
    if (row) {
      selectRow(row)
      return
    }
    // PO review 2026-09-22: saltar a otro componente desde la ficha tiene que funcionar aunque su fila
    // no este proyectada — con el preview de una linea por operacion, las demas no lo estan casi nunca.
    // Se destapa el material de esa operacion y la seleccion viaja en cuanto la fila existe.
    const jobKey = key.indexOf('/mat/') > 0 ? key.slice(0, key.indexOf('/mat/')) : ''
    if (jobKey) {
      setExpandedMaterialKeys((previous) => (previous.has(jobKey) ? previous : new Set(previous).add(jobKey)))
      setSelectedRowKey(key)
    }
  }, [rows, selectRow])

  // The operation dialog edits the row that was opened, which is always the selected one (it opened on
  // its second click). If the selection moved under it, the dialog is simply gone.
  /**
   * Que linea esta marcada en la pestana Material del panel, y bajo que fila del arbol vive — que es lo
   * que hace falta para que pulsar otra de la lista seleccione SU fila y no la de otra operacion que
   * consuma la misma linea de BOM.
   */
  const selectedMaterial = selectedRow?.kind === PROCESS_TREE_ROW_KIND.MATERIAL
    ? { lineKey: selectedRow.materialRow?.key || '', parentKey: selectedRow.parentKey }
    : { lineKey: '', parentKey: inspectedRow?.key || '' }

  const selectMaterialLine = (lineKey) => {
    if (selectedMaterial.parentKey) {
      selectRowByKey(selectedMaterial.parentKey + '/mat/' + lineKey)
    }
  }

  // La ventana sigue a la operacion inspeccionada: se abre sobre su fila, no sobre la linea marcada.
  selectedRowRef.current = selectedRow
  /*
   * B3 del Judge: el candidato se pone al dia SOLO mientras la fila marcada existe. Si se limpiara
   * tambien cuando no existe, se borraria justo en la pintada en la que la linea desaparece — que es
   * exactamente cuando hace falta para saber de quien colgaba — y el ascenso no llegaria a dispararse.
   * Cambiar de fila si lo limpia, que es lo que evita que el ascenso salte a una linea de hace tres
   * selecciones.
   */
  if (selectedRow) {
    liftCandidateRef.current = selectedRow.kind === PROCESS_TREE_ROW_KIND.MATERIAL ? selectedRow : null
  }

  /**
   * DILEMAS 9 y 12 (PO, 2026-09-23): cuando la linea de material marcada deja de estar en pantalla, la
   * seleccion SUBE a la operacion que la consume. Quedarse marcando una fila que ya no existe vacia el
   * panel tras una accion que no parecia destructiva.
   *
   * Una sola regla para los TRES caminos por los que puede desaparecer —apagar el boton Material, plegar
   * el conjunto que la contiene, y recoger el material de su propia operacion con el pie— en vez de un
   * disparador por camino. Lo primero que escribi fue lo segundo, y ademas fallaba en el caso que el
   * PO no habia pedido: recoger cuando la marcada es la que SE QUEDA, donde no hay que subir nada.
   * Mirar si sigue en pantalla no se puede equivocar en eso.
   */
  useEffect(() => {
    const row = liftCandidateRef.current
    // Solo asciende si la que se fue de la pantalla es ESTA, no una cualquiera (B3).
    if (!row || row.key !== selectedRowKey || visibleRows.some((visible) => visible.key === selectedRowKey)) {
      return
    }
    liftCandidateRef.current = null
    const jobRow = rows.find((candidate) => candidate.key === row.parentKey)
    if (!jobRow) {
      return
    }
    // Por `selectRow` y no por `setSelectedRowKey`: el ascenso tiene que llevarse tambien la seleccion
    // del grafo y el nodo actual. Sin eso `canEdit` se apagaba al ascender, porque compara el nodo
    // actual del reducer con el de la fila (B3).
    selectRow(jobRow)
  }, [visibleRows, selectedRowKey, rows, selectRow])

  const editingRow = editingRowKey !== '' && editingRowKey === inspectedRow?.key ? inspectedRow : null
  const catalogProps = { progressCalculationTypes, indexValues, workflowUseIndex, showMachineTime, featureGroupRevisionControlled, featureGroupBaselines, cascadeApprovalEnabled, fabricationOrderOperable, nightJobContext, workflowName, partReference, getTempUUID, setTempUUID }

  const searchPlaceholder = materialEnabled ? t_('Search by name, technology or component…') : t_('Search by name or technology…')

  const disconnectedLabels = warnings.incompleteProcess.labels
  const incompleteTotalLabels = warnings.incompleteTotal.labels
  const hasWarnings = disconnectedLabels.length > 0 || incompleteTotalLabels.length > 0

  const renderRowsBody = () => {
    if (visibleRows.length === 0) {
      return hasMatches
        ? <Typography data-testid='process-tree-empty' sx={{ p: 2, color: 'text.secondary' }}>{t_('This process has no operations')}</Typography>
        : <Typography data-testid='process-tree-no-matches' sx={{ p: 2, color: 'text.secondary' }}>{t_('No operations match the search')}</Typography>
    }
    return displayedRows.map((row, index) => (
      <ProcessTreeRow
        key={row.key}
        row={row}
        alt={index % 2 === 1}
        collapsed={isCollapsed(row)}
        selected={row.key === selectedRowKey}
        dependency={isDependencyRow(row)}
        showDuration={showDuration}
        onSelect={selectRow}
        onOpen={openRow}
        onJumpToNode={selectNodeById}
        onToggle={toggleRow}
        onExpandMaterial={expandMaterial}
        entering={enterIndexByKey.has(row.key)}
        enterIndex={enterIndexByKey.get(row.key) || 0}
        leaving={leaveIndexByKey.has(row.key)}
        leaveIndex={leaveIndexByKey.get(row.key) || 0}
      />
    ))
  }

  return (
    <Stack
      data-testid='process-tree-view'
      spacing={1}
      sx={{ p: 1, pt: headerSlotPx ? 0 : 1, minWidth: 0 }}
    >
      <Stack
        data-testid='process-tree-toolbar'
        direction='row'
        spacing={1}
        alignItems='center'
        flexWrap='wrap'
        sx={headerSlotPx ? { minHeight: SURFACE_BAR_HEIGHT_PX, pr: headerSlotPx + 'px' } : undefined}
      >
        {/* RN44.1: the placeholder is the only declaration of which criteria are alive — the box is never disabled. */}
        <TextField
          size='small'
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          inputProps={{ 'data-testid': 'process-tree-search', 'aria-label': searchPlaceholder }}
          sx={{ minWidth: '18rem' }}
        />
        <Divider orientation='vertical' flexItem />
        <Button size='small' variant='outlined' data-testid='process-tree-collapse-toggle' onClick={toggleAllRows} sx={{ textTransform: 'none' }}>
          {anyCollapsed ? t_('Expand all') : t_('Collapse sets')}
        </Button>
        {/* RN51: without RESOURCES the button does not exist — the BOM is never asked for (S89). */}
        {showMaterial
          ? (
            <Tooltip title={t_('Material')}>
              <ToggleButton
                size='small'
                value='material'
                selected={materialEnabled}
                data-testid='process-tree-material-toggle'
                aria-label={t_('Material')}
                onChange={toggleMaterial}
                sx={{
                  // El texto se fue, asi que el nombre accesible lo pone el aria-label. Encendido va en
                  // verde, que es lo que dice de un vistazo si el material esta cargado.
                  px: 1,
                  color: materialEnabled ? 'primary.main' : 'text.secondary',
                  borderColor: materialEnabled ? 'primary.main' : undefined,
                  '& svg': { fontSize: 18 }
                }}
              >
                <CategoryRoundedIcon fontSize='inherit' />
              </ToggleButton>
            </Tooltip>
            )
          : null}
        {/* RN33: las cifras del proceso viven en la barra, a la derecha del todo, no en una linea propia. */}
        <Box sx={{ flexGrow: 1 }} />
        <Stack data-testid='process-tree-header' direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
          <Typography data-testid='process-tree-total-count' sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {t_('{{count}} operations', { count: totals.jobCount })}
          </Typography>
          {/* RN34: the accumulated clock, which does not wrap at 24 h (risk R5). */}
          {showDuration
            ? (
              <>
                <Divider orientation='vertical' flexItem />
                <Typography data-testid='process-tree-total-time' sx={{ color: 'text.secondary', fontSize: 14 }}>
                  {t_('Total time')}: <Box component='span' sx={{ fontFamily: 'monospace', color: 'text.primary' }}>{GetAccumulatedUtcTime(totals.totalTimeMs)}</Box>
                </Typography>
              </>
              )
            : null}
        </Stack>
      </Stack>

      {/* The dock sizes itself, so the tree simply takes whatever is left — no fixed column any more. */}
      {/*
        * #215 (D3): el alto lo pone el usuario con la barra, no el panel. La fila tiene `height`, no
        * `minHeight`: el arbol y el panel se desplazan por dentro y la fila no crece al anadir tareas.
        * Fila y barra van juntas en una caja para que el `spacing` del Stack no las separe.
        */}
      <Box>
        <Box ref={surfaceRef} data-testid='process-tree-surface-row' style={{ height: surfaceHeight }} sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/* RN24: the columns are fixed and carry NO ordering control — the order is the process. */}
            <Box
              data-testid='process-tree-column-header'
              sx={{
                display: 'grid',
                gridTemplateColumns: showDuration
                  ? 'minmax(0, 34rem) 118px 104px 148px minmax(0, 1fr)'
                  : 'minmax(0, 34rem) 118px 148px minmax(0, 1fr)',
                columnGap: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                fontWeight: 600,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'text.secondary'
              }}
            >
              <Box style={{ paddingLeft: (BRANCH_COL_PX + 18) + 'px' }}>{t_('Operation')}</Box>
              <Box>{t_('Technology')}</Box>
              {showDuration ? <Box sx={{ textAlign: 'right' }}>{t_('Duration')}</Box> : null}
              <Box sx={{ textAlign: 'right' }}>{t_('Material')}</Box>
            </Box>
            {/*
              * B5 del Judge (2026-09-23), MEDIDO en Chrome: con el alto de la fila dictado por el panel,
              * el arbol NO se desplazaba por dentro — con 97 filas medía 4462 px y estiraba la pagina,
              * el defecto que el PO reporto el 23/09. `flex: 1` con `min-height: 0` no basta: en un
              * contenedor de alto automatico, el hijo sigue aportando su contenido al calculo.
              *
              * El hueco saca las filas DEL FLUJO (`position: absolute`), asi que el arbol aporta a la
              * fila solo su barra y su cabecera. Medido: 342 px con 5, 40 y 97 filas, desplazando por
              * dentro a partir de las 40, y el panel sigue dictando el alto (200 -> 228, 700 -> 728).
              * Desde la #215 el alto ya no lo dicta el panel sino la barra, y el hueco sigue haciendo falta
              * para que las filas se desplacen dentro de ese alto.
              */}
            <Box sx={{ flex: '1 1 0', minHeight: 0, position: 'relative' }}>
              <Box ref={rowsRef} data-testid='process-tree-rows' sx={{ position: 'absolute', inset: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                {renderRowsBody()}
              </Box>
            </Box>
          </Box>
          <ProcessTreeInspectorDock>
            <ProcessTreeInspector
              node={selectedNode}
              // La linea marcada dentro de la pestana Material, y a donde va el clic en otra de la lista.
              materialSelectedKey={selectedMaterial.lineKey}
              onSelectMaterialLine={selectMaterialLine}
              nested={selectedNested}
              levelNodes={selectedLevelNodes}
              branchIndex={inspectedRow?.branchIndex || 0}
              branchCount={inspectedRow?.branchCount || 0}
              depth={inspectedRow?.depth || 0}
              coverage={inspectorCoverage}
              trail={selectedTrail}
              showMaterial={showMaterial}
              canEdit={canEdit}
              canEditTasks={canEditTasks}
              editHint={editHint}
              requiredNodes={requiredNodes}
              onUpdateJobReference={writeJobReference}
              onUpdateTasks={writeTaskList}
              onUpdateNodeName={updateNodeName}
              onUpdateNodeCode={updateNodeCode}
              onUpdateTechnology={updateTechnology}
              branch={selectedBranch}
              onSelectRowKey={selectRowByKey}
              showCounters={showDuration}
              showTasks={showTasks}
              onSelectNode={selectNodeById}
              onEditingRowChanged={onEditingRowChanged}
              onManageResources={() => {
                // RN146.2: the same window the second click opens, but on the tab that was asked for.
                // Se abre sobre la fila de la OPERACION: con una linea de material marcada, `selectedRowKey`
                // es la linea, y la ventana acababa colgando de una fila que no es un job.
                setEditingTab(TAB.MATERIAL)
                setEditingRowKey(inspectedRow?.key || selectedRowKey)
              }}
              {...catalogProps}
            />
          </ProcessTreeInspectorDock>
        </Box>
        <SurfaceResizeHandle height={surfaceHeight} surfaceRef={surfaceRef} onHeightChange={onSurfaceHeightChange} />
      </Box>

      {/* PO review 2026-09-11: the operation editor, opened by the second click on the selected job row. */}
      <ProcessTreeJobDialog
        open={editingRow !== null}
        node={editingRow ? selectedNode : null}
        trail={selectedTrail}
        depth={editingRow?.depth || 0}
        branchIndex={editingRow?.branchIndex || 0}
        branchCount={editingRow?.branchCount || 0}
        coverage={inspectorCoverage}
        showCounters={showDuration}
        showTasks={showTasks}
        showMaterial={showMaterial}
        editable={canEdit}
        editableTasks={canEditTasks}
        editHint={editHint}
        requiredNodes={requiredNodes}
        onClose={() => setEditingRowKey('')}
        onUpdateJobReference={writeJobReference}
        onUpdateTasks={writeTaskList}
        onUpdateNodeName={updateNodeName}
        onUpdateNodeCode={updateNodeCode}
        onUpdateTechnology={updateTechnology}
        onSelectNode={selectNodeById}
        initialTab={editingTab}
        editableMaterial={canEditMaterial}
        nested={selectedNested}
        resourceTypes={resourceTypes}
        showAlert={showAlert}
        parentPartReference={parentPartReference}
        partReferenceId={parentPartReference?.id}
        workflow={workflow}
        onEditingRowChanged={onEditingRowChanged}
        {...catalogProps}
      />

      {/* RN71/POL1: the warnings live below the tree and never replace it. */}
      {hasWarnings
        ? (
          <Stack data-testid='process-tree-footer' spacing={1}>
            {disconnectedLabels.length > 0
              ? (
                <Alert severity='warning' data-testid='process-tree-warning-incomplete-process'>
                  {t_('Some operations are not connected to the process')}: {disconnectedLabels.join(', ')}
                </Alert>
                )
              : null}
            {incompleteTotalLabels.length > 0
              ? (
                <Alert severity='warning' data-testid='process-tree-warning-incomplete-total'>
                  {t_('The total is incomplete: some sets could not be loaded')}: {incompleteTotalLabels.join(', ')}
                </Alert>
                )
              : null}
          </Stack>
          )
        : null}
    </Stack>
  )
}

export default ProcessTreeView
